import { FC, forwardRef, Ref } from 'react';
import * as RadixAvatar from '@radix-ui/react-avatar';
import cn from 'classnames';

import { Icon, IconProps } from '../../atoms/icon/Icon';

import './avatar.scss';

export type AvatarType = 'user-photo' | 'user-icon' | 'user-initials';

export type AvatarSize = 'small' | 'medium' | 'large' | 'extra-large';

export const AvatarRoot = RadixAvatar.Root;

export interface AvatarImageProps extends RadixAvatar.AvatarImageProps {
  /**
   * User image
   */
  src?: string;
  /**
   * User image alt text
   */
  alt?: string;
}
export const AvatarImage: FC<RadixAvatar.AvatarImageProps> = (props) => <RadixAvatar.Image {...props} />;

export const AvatarFallback = RadixAvatar.Fallback;

export interface AvatarProps extends RadixAvatar.AvatarProps {
  /**
   * Define avatar type
   * @default user-icon
   */
  avatarType: AvatarType;
  /**
   * User image
   */
  imageURL?: string;
  /**
   * User initials
   */
  userInitials?: string;
  /**
   * Determines avatar size
   * @default medium
   */
  avatarSize?: AvatarSize;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * The ref to the HTML DOM element
   */
  ref?: Ref<HTMLDivElement>;
}

const AvatarComponent = forwardRef<HTMLDivElement, AvatarProps>(
  (
    { avatarType, imageURL, userInitials, avatarSize = 'medium', classNames = [], ...props }: AvatarProps,
    ref: AvatarProps['ref'],
  ) => {
    const classAvatar = 'lsux-avatar';
    const cnAvatar = cn(
      {
        [`${classAvatar}`]: true,
        [`${classAvatar}--type-${avatarType}`]: avatarType,
        [`${classAvatar}--size-${avatarSize}`]: avatarSize,
      },
      classNames,
    );

    const iconSize: Record<AvatarSize, IconProps['size']> = {
      'extra-large': 'large',
      large: 'large',
      medium: 'medium',
      small: 'small',
    };

    const renderUserPhoto = () => (
      <>
        <AvatarImage className={`${classAvatar}__image`} src={imageURL} alt="user avatar" />
        <AvatarFallback className={`${classAvatar}__fallback`}>
          {userInitials || <Icon name="action_user_single" color="N700" size={iconSize[avatarSize]} />}
        </AvatarFallback>
      </>
    );

    const renderUserInitials = () => <div className={`${classAvatar}__fallback`}>{userInitials}</div>;

    const renderUserIcon = () => <Icon name="action_user_single" color="N700" size={iconSize[avatarSize]} />;

    const avatarRenderers = {
      'user-icon': renderUserIcon,
      'user-initials': renderUserInitials,
      'user-photo': renderUserPhoto,
    };

    return (
      <AvatarRoot {...props} className={cnAvatar} ref={ref}>
        {avatarRenderers[avatarType]?.()}
      </AvatarRoot>
    );
  },
) as FC<AvatarProps>;

AvatarComponent.displayName = 'Avatar';

type AvatarRootComponent = FC<RadixAvatar.AvatarProps>;
type AvatarImageComponent = FC<AvatarImageProps>;
type AvatarFallbackComponent = FC<RadixAvatar.AvatarFallbackProps>;

type AvatarComp = FC<AvatarProps> & {
  Root: AvatarRootComponent;
  Image: AvatarImageComponent;
  Fallback: AvatarFallbackComponent;
};

export const Avatar = AvatarComponent as AvatarComp;

Avatar.Root = AvatarRoot;
Avatar.Image = AvatarImage;
Avatar.Fallback = AvatarFallback;
